import { useEffect } from 'react'
import styled from 'styled-components'
import { Controller, useForm } from 'react-hook-form'
import { Row } from '@shipt/design-system-layouts'
import { Body, typographyAttributes } from '@shipt/design-system-typography'
import { TextInput } from '@shipt/design-system-inputs'
import { Button, TextButton } from '@shipt/design-system-buttons'
import { usePopover, Popover } from '@shipt/design-system-popovers'
import { AddressIcon, InfoCircleIcon } from '@shipt/design-system-icons'
import { HDivider } from '@/features/shared/components/Dividers'
import { CloseButton } from '@/features/shared/components/CloseButton'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMediaQuery } from '@/features/shared/hooks/useMediaQuery'
import { useGuestParams, useSetGuestStoreCookie } from '@/features/account/services/GuestUser/hooks'
import { getGuestStoreParams } from '@/features/shared/utils/dataFetching/guestStoreParams'
import { type PopoverReferenceProps } from '@/features/shared/utils/types'
import { type ShoppingStoreDetails } from '@/features/shop/services/ShoppingStore/types'
import { zipValidation } from '@/features/account/components/AddressForm/validations'
import { useOpenGuestAddressModalForPLA } from '@/features/shop/components/ProductDetail/PLA/hooks'
import { trackElementClicked, trackElementViewed } from '@/features/shared/analytics/element'
import { type PLAGuestWithZipOnlyHeaderOnSubmit } from '@/features/shared/components/Headers/PLAGuestWithZipOnlyHeader'
import { useMutation } from '@tanstack/react-query'
import { z } from 'zod'
import { useMutationFetchShoppingStores } from '@/features/shop/services/ShoppingStore/mutations'
import { useMutationSetGuestAddressCookie } from '@/features/shared/hooks/useMutationSetGuestAddressCookie'
import { theme } from '@/features/shared/constants/shiptTheme'

const zipSchema = z.object({ zip_code: zipValidation })
type FieldValues = { zip_code: string }
type Props = {
  onSubmit: PLAGuestWithZipOnlyHeaderOnSubmit
  children: (props: PopoverReferenceProps) => React.ReactNode
  displaySublocation: string
}

export const ZipConfirmationPopover = ({ onSubmit, children, displaySublocation }: Props) => {
  const { screenSm } = useMediaQuery()
  const { guestStore } = useGuestParams() ?? {}
  const setGuestStoreCookie = useSetGuestStoreCookie()
  const { mutateAsync: setGuestAddressCookie } = useMutationSetGuestAddressCookie()
  const onSubmitMutation = useMutation({ mutationFn: onSubmit })
  const { getReferenceProps, getPopoverProps, isPopoverOpen, setIsPopoverOpen } = usePopover({
    modal: false,
    initialFocus: -1,
    align: screenSm ? 'center' : 'end',
  })
  const { handleSubmit, control, formState, setError, reset } = useForm<FieldValues>({
    resolver: zodResolver(zipSchema),
  })

  useEffect(() => {
    if (isPopoverOpen) {
      trackElementViewed({ type: 'popover', message_goal: 'zip_confirmation' })
    }
  }, [isPopoverOpen])

  useEffect(() => {
    if (!isPopoverOpen || formState.isSubmitSuccessful) reset()
  }, [isPopoverOpen, formState.isSubmitSuccessful, reset])

  const { mutateAsync: fetchStores, isPending: isLoadingStores } = useMutationFetchShoppingStores()
  const isLoading = isLoadingStores || onSubmitMutation.isPending

  const onSetError = (message: string) => setError('zip_code', { message })

  const onSubmitZip = async ({ zip_code }: FieldValues) => {
    trackElementClicked({
      type: 'button',
      message_goal: 'zip_confirmation',
      content: 'click_confirm',
    })
    let store: ShoppingStoreDetails | undefined
    try {
      const stores = await fetchStores({ address: { zip_code } })
      store = stores.delivery_stores.find((s) => s.id === guestStore?.id)
      if (!store) {
        return onSetError(`${guestStore?.name} not available within ZIP code`)
      }
    } catch {
      return onSetError('ZIP code is not yet covered by Shipt')
    }
    try {
      await onSubmitMutation.mutateAsync({
        storeParams: getGuestStoreParams({
          guestStore: store,
          guestAddress: { zip_code },
        }),
        store,
      })
      await setGuestAddressCookie({ zip_code })
      setGuestStoreCookie(store)
      setIsPopoverOpen(false)
    } catch (e) {
      if (e instanceof Error) {
        onSetError(e.message)
      }
    }
  }

  const handleOnClickReference = () => {
    trackElementClicked({
      type: 'button',
      display_sublocation: displaySublocation,
      message_goal: 'open_zip_confirmation_popover',
    })
  }
  const openGuestAddressModal = useOpenGuestAddressModalForPLA()

  return (
    <>
      {children({
        ...getReferenceProps({ onClick: handleOnClickReference }),
        ...typographyAttributes({ isPII: true }),
      })}
      <div>
        <Popover {...getPopoverProps()} size="md">
          <Row align="center" justify="space-between" margin={[0, 0, 'lg', 0]}>
            <Body strong size="xl">
              Update your delivery ZIP code
            </Body>
            <PopoverCloseButton
              aria-label="Close popover"
              onClick={() => setIsPopoverOpen(false)}
            />
          </Row>
          <ZipForm onSubmit={handleSubmit(onSubmitZip)}>
            <Controller
              render={({ field }) => (
                <ZipInput
                  size="md"
                  maxLength={5}
                  leading={<AddressIcon />}
                  errorMessage={formState.errors.zip_code?.message || ''}
                  {...field}
                />
              )}
              name="zip_code"
              control={control}
              rules={{ required: true }}
              defaultValue=""
            />
            <Button type="submit" size="md" loading={isLoading} disabled={isLoading}>
              Confirm
            </Button>
          </ZipForm>
          <TextButton onClick={openGuestAddressModal}>Enter delivery address instead</TextButton>
          <HDivider />
          <Row align="center" spacing="md">
            <PopoverInfoCircleIcon size="md" />
            <Body size="md">
              You discovered this page from an external link. Please verify the ZIP code.
            </Body>
          </Row>
        </Popover>
      </div>
    </>
  )
}

const ZipForm = styled.form`
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
`

const ZipInput = styled(TextInput)<{ errorMessage: string }>`
  display: ${({ errorMessage }) => (errorMessage ? 'block' : 'flex')};

  & input {
    width: 100%;
  }
`

const PopoverCloseButton = styled(CloseButton)`
  color: ${theme.color.content.base.neutral.primary};
`

const PopoverInfoCircleIcon = styled(InfoCircleIcon)`
  flex-shrink: 0;
`
